import React, { createContext, useState, useEffect, useContext } from "react";
import { SINGIN_API } from "./APIHooks";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // --- SIGN IN ---
  const signIn = async (email, password) => {
    try {
      const apiKey = process.env.REACT_APP_API_KEY; // Access the API key from the environment variable
      const response = await fetch(SINGIN_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiKey}`, // Include the API key in the headers
        },
        body: JSON.stringify({
          emailaddress: email,
          password: password,
        }),
      });

      const data = await response.json();

      if (data.status === "success") {
        const userData = {
          user_id: data.data.user_id,
          name: data.data.name,
          emailaddress: data.data.emailaddress,
          phonenumber: data.data.phonenumber,
          role: data.data.role,
          sex: data.data.sex,
          address: data.data.address,
          is_active: data.data.is_active,
        };
        localStorage.setItem("userToken", JSON.stringify(userData));
        localStorage.setItem("lastActivity", Date.now()); // store login time
        setUser(userData);
        return { success: true };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error(error);
      return {
        success: false,
        message: "An error occurred. Please try again.",
      };
    }
  };

  // --- SIGN OUT ---
  const signOut = (reason) => {
    localStorage.removeItem("userToken");
    localStorage.removeItem("lastActivity");
    setUser(null);
    if (reason) alert(reason);
  };

  // --- LOAD USER FROM STORAGE ---
  const loadUser = () => {
    const userToken = localStorage.getItem("userToken");
    if (userToken) {
      setUser(JSON.parse(userToken));
    }
    setLoading(false);
  };

  // --- AUTO LOGOUT IF INACTIVE FOR 30 MINUTES ---
  useEffect(() => {
    if (!user) return;

    const checkInactivity = () => {
      const lastActivity = localStorage.getItem("lastActivity");
      if (!lastActivity) return;

      const now = Date.now();
      const diff = now - parseInt(lastActivity, 10);

      if (diff > 30 * 60 * 1000) {
        // 30 minutes
        signOut("Session expired. Please log in again.");
      }
    };

    const interval = setInterval(checkInactivity, 60 * 1000); // check every 1 minute
    return () => clearInterval(interval);
  }, [user]);

  // --- RESET ACTIVITY TIMER ON USER ACTIONS ---
  useEffect(() => {
    if (!user) return;

    const updateActivity = () => {
      localStorage.setItem("lastActivity", Date.now());
    };

    const events = ["click", "mousemove", "keydown", "scroll", "touchstart"];
    events.forEach((event) => window.addEventListener(event, updateActivity));

    return () => {
      events.forEach((event) =>
        window.removeEventListener(event, updateActivity)
      );
    };
  }, [user]);

  // --- INITIAL USER LOAD ---
  useEffect(() => {
    loadUser();
  }, []);

  return (
    <AuthContext.Provider value={{ user, signIn, signOut, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
